import type {ReactNode} from 'react'
import React, {useMemo, useCallback, useRef} from 'react'
import {DataTable as PepsicoDataTable} from 'pepsico-ds'
import classNames from 'classnames'

// Type definitions for better type safety
export interface Column {
  columnKey: string
  header: string | (() => React.ReactElement)
  cellData?: (info: any) => ReactNode
  minSize?: number
  maxSize?: number
  initialSize?: number
  columnAlignment?: string
  enableSorting?: boolean
  columnGroup?: string
}

interface ExpandableRow {
  subRows?: any[]
  [key: string]: any
}

/**
 * Props interface for the DataTable component
 */
export interface DataTableProps {
  /** Data array for the table */
  data: ExpandableRow[]
  /** Column definitions */
  columns: Column[]
  /** Size variant for the table */
  size?: 'small' | 'medium' | 'large'
  /** Enable sorting functionality */
  enableSorting?: boolean
  /** Enable row expansion */
  isExpandable?: boolean
  /** Custom function to get a unique row ID */
  getRowId?: (row: any) => string | number
  /** Function to get sub rows for expandable rows */
  getSubRows?: (originalRow: any) => any[]
  /** Function to determine if a row can be expanded */
  getRowCanExpand?: (originalRow: any) => boolean
  /** Additional CSS class names */
  className?: string
  /** Number of rows per page */
  pageSize?: number
  /** Hide the table footer and pagination elements */
  hideTableFooter?: boolean
  /** Hide the table header toolbar and divider (but not the column headers themselves) */
  hideHeader?: boolean
  /** Hide just the table toolbar without affecting the header */
  hideTableToolbar?: boolean
  /** Hide the pagination arrows in the toolbar */
  hideToolbarPagination?: boolean
  /** Force show pagination even when data is less than 10 rows */
  forceShowPagination?: boolean
  /** Filters for the table */
  filters?: any[]
  /** Current page index */
  pageIndex?: number
  /** Whether the table is in loading state */
  isLoading?: boolean
  /** Maximum height of the table */
  maxHeight?: string
  /** Handler for page index changes */
  onPageIndexChange?: (pageIndex: number) => void
  /** Whether to show the filter toolbar */
  showFilterToolbar?: boolean
  /** Whether to show the search option */
  showSearchOption?: boolean
  /** Whether filtering is handled by the caller */
  manualFiltering?: boolean
  /** Placeholder text for the search input */
  searchPlaceholder?: string
  /** Handler for search input changes */
  onSearch?: (value: string) => void
  /** Callback function to be called when the global search changes */
  onGlobalSearchChange?: (value: string) => void
  /** Table description text */
  tableDescription?: string
  /** Enable text selection in the table (default: false) */
  enableTextSelection?: boolean
  /** Whether to show the table settings gear icon */
  showTableSettings?: boolean
  /** Callback fired when column visibility changes via Table Settings (GDS) */
  onColumnVisibilityChange?: (visibility: Record<string, boolean>) => void
  /** Callback fired when column order changes via Table Settings (GDS) */
  onColumnOrderChange?: (order: string[]) => void
  /** Any other props */
  [key: string]: any
}

/**
 * DataTable - A consolidated component that wraps the pepsico-ds DataTable component
 * This component combines the functionality of DataTableDisplay and DataTableWrapper
 * into a single, more maintainable component.
 */
// Default getRowId function - defined outside component to maintain stable reference
const defaultGetRowId = (row: any) => row.id

const DataTable: React.FC<DataTableProps> = ({
  data,
  columns,
  size = 'medium',
  enableSorting = false,
  isExpandable = false,
  getRowId = defaultGetRowId,
  getSubRows,
  getRowCanExpand,
  className = '',
  pageSize = 10,
  hideTableFooter = false,
  hideHeader = true,
  hideTableToolbar = false,
  hideToolbarPagination = true,
  forceShowPagination = false,
  filters = [],
  pageIndex = 0,
  isLoading = false,
  maxHeight,
  onPageIndexChange,
  showFilterToolbar = false,
  showSearchOption = false,
  searchPlaceholder = '',
  onSearch,
  onGlobalSearchChange,
  tableDescription = '',
  columnPinningState,
  enableTextSelection = false,
  showTableSettings = false,
  onColumnVisibilityChange,
  onColumnOrderChange,
  ...rest
}) => {
  // Memoize the table data to avoid unnecessary re-renders
  const tableData = useMemo(() => {
    if (!Array.isArray(data)) {
      console.warn('DataTable: data is not an array', data)
      return []
    }
    // Add id to each row using getRowId and process subRows if present
    return data.map(row => ({
      ...row,
      id: getRowId(row),
      // If row has subRows, process them too
      ...(row.subRows
        ? {
            subRows: row.subRows.map(subRow => ({
              ...subRow,
              id: getRowId(subRow),
            })),
          }
        : {}),
    }))
  }, [data, getRowId])

  // Map the columns to the format expected by the pepsico-ds DataTable
  const tableColumns = useMemo(() => {
    return columns.map(col => {
      return {
        ...col,
        cellData:
          col.cellData ||
          ((info: any) => {
            return info.getValue ? info.getValue() : info.row.original[col.columnKey]
          }),
      }
    })
  }, [columns])

  // Automatically hide table footer if less than 10 rows, unless explicitly set or forceShowPagination is true
  const shouldHideTableFooter = hideTableFooter || (data.length <= 10 && !forceShowPagination)

  // Define a custom class to add to the DataTable component based on props
  const dataTableCustomClass = classNames({
    'hide-header': hideHeader,
    'hide-table-footer': shouldHideTableFooter,
    'hide-table-toolbar': hideTableToolbar,
    'hide-toolbar-pagination': hideToolbarPagination,
    'select-none': !enableTextSelection,
  })

  // Combine classes, adding utility classes based on props
  const tableClasses = classNames(className, dataTableCustomClass)

  // Generate a key based on column headers to force GDS component re-render when headers change
  const tableKey = useMemo(() => tableColumns.map(col => `${col.columnKey}:${col.header}`).join('|'), [tableColumns])

  // Ref to the wrapper div so we can scroll the inner pepsico-ds scrollable container on page change
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Track whether the component has completed its initial render.
  // pepsico-ds fires onPageIndexChange on mount/remount with the initial page index;
  // we must skip the scroll for that invocation to avoid affecting sibling tables
  // that remount due to shared state changes.
  const hasMountedRef = useRef(false)

  // Wrap onPageIndexChange to scroll the table to top when the page changes
  const handlePageIndexChange = useCallback(
    (newPageIndex: number) => {
      if (!hasMountedRef.current) {
        hasMountedRef.current = true
        onPageIndexChange?.(newPageIndex)
        return
      }
      if (maxHeight !== undefined && maxHeight !== '' && wrapperRef.current) {
        // The pepsico-ds DataTable renders a root div.datatable with overflow:auto
        // and inline maxHeight when the maxHeight prop is set. Scroll it to the top.
        const scrollableEl = wrapperRef.current.querySelector<HTMLElement>('.datatable')
        if (scrollableEl) {
          scrollableEl.scrollTop = 0
        }
        // Scroll the page so the table container is visible at the top of the viewport.
        // This prevents the user from being stranded at the bottom of the section
        // (or worse, shifted to a sibling section below) after paginating.
        wrapperRef.current.scrollIntoView({behavior: 'smooth', block: 'start'})
      }
      onPageIndexChange?.(newPageIndex)
    },
    [maxHeight, onPageIndexChange],
  )

  return (
    <div className={tableClasses} ref={wrapperRef}>
      <PepsicoDataTable
        key={tableKey}
        data={tableData}
        columns={tableColumns}
        filters={filters}
        pageIndex={pageIndex}
        pageSize={pageSize}
        isExpandable={isExpandable}
        isLoading={isLoading}
        maxHeight={maxHeight}
        enableSorting={enableSorting}
        getSubRows={getSubRows}
        getRowCanExpand={getRowCanExpand}
        showFilterToolbar={showFilterToolbar}
        showColumnFilters={false}
        showSearchOption={showSearchOption}
        searchPlaceholder={searchPlaceholder}
        onGlobalSearchChange={onGlobalSearchChange}
        size={size}
        tableDescription={tableDescription}
        columnPinningState={columnPinningState}
        onPageIndexChange={handlePageIndexChange}
        showTableSettings={showTableSettings}
        onColumnVisibilityChange={onColumnVisibilityChange}
        onColumnOrderChange={onColumnOrderChange}
        {...rest}
      />
    </div>
  )
}

// Wrap with React.memo for performance optimization
export default React.memo(DataTable)
